import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, Ticket, Share2, Download } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import QRCode from 'qrcode.react';

const CheckoutSuccess = () => {
  const { entryId } = useParams();
  const [entry, setEntry] = useState<any>(null);
  const [campaign, setCampaign] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!entryId) {
      setError('Invalid entry ID');
      setLoading(false);
      return;
    }
    fetchEntryDetails();
  }, [entryId]);

  const fetchEntryDetails = async () => {
    try {
      const { data: entryData, error: entryError } = await supabase
        .from('entries')
        .select('*, campaigns(*)')
        .eq('id', entryId)
        .single();

      if (entryError) throw entryError;
      if (!entryData) throw new Error('Entry not found');

      setEntry(entryData);
      setCampaign(entryData.campaigns);
      setLoading(false);
    } catch (error: any) {
      console.error('Error:', error);
      setError(error.message || 'Failed to load entry details');
      setLoading(false);
    }
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `My Raffle Ticket for ${campaign?.name}`,
          text: `Check out my raffle ticket for ${campaign?.name}!`,
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const downloadTicket = () => {
    const canvas = document.getElementById('ticket-qr') as HTMLCanvasElement;
    if (!canvas) return;

    const link = document.createElement('a');
    link.download = `raffle-ticket-${entry?.ticket_number}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  if (loading) {
    return (
      <div className="max-w-md mx-auto bg-white rounded-xl shadow-lg p-8 text-center">
        <div className="animate-spin h-8 w-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
        <p>Loading ticket details...</p>
      </div>
    );
  }

  if (error || !entry || !campaign) {
    return (
      <div className="max-w-md mx-auto bg-white rounded-xl shadow-lg p-8 text-center">
        <div className="text-red-500 mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">Error</h2>
        <p className="text-gray-600 mb-6">{error}</p>
        <Link to="/" className="inline-block bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 transition-colors">
          Return Home
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-white rounded-xl shadow-lg p-8">
      <div className="text-center mb-6">
        <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Successful!</h2>
        <p className="text-gray-600">
          Thank you for your purchase. Your raffle tickets have been confirmed.
        </p>
      </div>

      <div className="bg-gray-50 p-6 rounded-lg mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Ticket Details</h3>
          <Ticket className="h-5 w-5 text-purple-500" />
        </div>
        
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600">Ticket Number:</span>
            <span className="font-semibold">{entry.ticket_number}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Campaign:</span>
            <span className="font-semibold">{campaign.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Quantity:</span>
            <span className="font-semibold">{entry.quantity}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Total Amount:</span>
            <span className="font-semibold">${entry.total_amount?.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Purchase Date:</span>
            <span className="font-semibold">{new Date(entry.created_at).toLocaleDateString()}</span>
          </div>
        </div>
      </div>

      <div className="text-center mb-6">
        <div className="bg-white p-4 rounded-lg inline-block mb-4 border">
          <QRCode
            id="ticket-qr"
            value={`${window.location.origin}/ticket/${entry.ticket_number}`}
            size={200}
            level="H"
            includeMargin={true}
          />
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Scan this QR code to view your ticket details
        </p>
        <div className="flex justify-center space-x-4">
          <button
            onClick={handleShare}
            className="flex items-center px-4 py-2 bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 transition-colors"
          >
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </button>
          <button
            onClick={downloadTicket}
            className="flex items-center px-4 py-2 bg-purple-100 text-purple-700 rounded-md hover:bg-purple-200 transition-colors"
          >
            <Download className="h-4 w-4 mr-2" />
            Download
          </button>
        </div>
      </div>

      <div className="text-center">
        <Link
          to="/"
          className="inline-block bg-purple-600 text-white py-2 px-6 rounded-md hover:bg-purple-700 transition-colors"
        >
          Return Home
        </Link>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
